import {
  AppBar as MuiAppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  useTheme,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  useMediaQuery,
  Badge,
  Tooltip,
  alpha,
  Chip,
  Paper,
  ListItemIcon,
  ListItemText,
  Switch,
  Stack,
  Fade,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Brightness4,
  Brightness7,
  AccountCircle,
  ExitToApp,
  Settings,
  Dashboard,
  CalendarToday,
  Description,
  CameraAlt,
  People,
  Notifications,
  KeyboardArrowDown,
  Person,
  Help,
  Feedback,
  VerifiedUser,
  Close as CloseIcon,
} from "@mui/icons-material";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";

export default function AppBar({ toggleTheme, isDarkMode }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const [anchorEl, setAnchorEl] = useState(null);
  const [notificationAnchor, setNotificationAnchor] = useState(null);
  const [profileAnchor, setProfileAnchor] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogTitle, setDialogTitle] = useState("");
  const [dialogContent, setDialogContent] = useState("");
  const [dialogIcon, setDialogIcon] = useState(null);

  const open = Boolean(anchorEl);
  const notificationOpen = Boolean(notificationAnchor);
  const profileOpen = Boolean(profileAnchor);

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationMenu = (event) => {
    setNotificationAnchor(event.currentTarget);
  };

  const handleNotificationClose = () => {
    setNotificationAnchor(null);
  };

  const handleProfileMenu = (event) => {
    setProfileAnchor(event.currentTarget);
  };

  const handleProfileClose = () => {
    setProfileAnchor(null);
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    navigate("/login");
    handleClose();
    handleProfileClose();
  };

  // Handle dialog open
  const handleDialogOpen = (title, content, icon) => {
    setDialogTitle(title);
    setDialogContent(content);
    setDialogIcon(icon);
    setDialogOpen(true);
    handleProfileClose();
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  // Navigation items
  const navItems = [
    { path: "/", label: "Dashboard", icon: <Dashboard /> },
    { path: "/calendar", label: "Kalender", icon: <CalendarToday /> },
    { path: "/documents", label: "Foto", icon: <CameraAlt /> },
    { path: "/meetings", label: "Rapat", icon: <People /> },
  ];

  // Notification data
  const notifications = [
    {
      id: 1,
      title: "Rapat baru ditambahkan",
      time: "5 menit yang lalu",
      type: "meeting",
      read: false,
    },
    {
      id: 2,
      title: "Dokumen telah diupdate",
      time: "1 jam yang lalu",
      type: "document",
      read: false,
    },
    {
      id: 3,
      title: "Pengingat rapat besok",
      time: "3 jam yang lalu",
      type: "reminder",
      read: true,
    },
  ];

  const unreadCount = notifications.filter((n) => !n.read).length;

  const isActiveRoute = (path) => {
    return location.pathname === path;
  };

  // Dialog content for each menu
  const dialogData = {
    profile: {
      title: "👤 Profil User",
      content:
        "Halaman profil akan menampilkan informasi user seperti nama, email, role, dan pengaturan akun lainnya.",
      icon: <AccountCircle sx={{ fontSize: 48, color: "#2e7d32" }} />,
    },
    settings: {
      title: "⚙️ Pengaturan",
      content:
        "Halaman pengaturan untuk mengatur preferensi aplikasi, tema, notifikasi, dan konfigurasi lainnya.",
      icon: <Settings sx={{ fontSize: 48, color: "#1976d2" }} />,
    },
    help: {
      title: "❓ Bantuan",
      content:
        "Halaman bantuan berisi panduan penggunaan aplikasi, FAQ, dan informasi kontak dukungan.",
      icon: <Help sx={{ fontSize: 48, color: "#ff9800" }} />,
    },
    feedback: {
      title: "💬 Kritik & Saran",
      content:
        "Kirimkan kritik dan saran Anda untuk pengembangan aplikasi yang lebih baik. Tim kami akan merespon dengan cepat.",
      icon: <Feedback sx={{ fontSize: 48, color: "#9c27b0" }} />,
    },
  };

  return (
    <>
      <MuiAppBar
        position="sticky"
        elevation={scrolled ? 4 : 1}
        sx={{
          mb: 4,
          bgcolor: scrolled
            ? alpha(theme.palette.background.paper, 0.95)
            : "background.paper",
          color: "text.primary",
          borderBottom: `1px solid ${theme.palette.divider}`,
          backdropFilter: scrolled ? "blur(10px)" : "none",
          transition: "all 0.3s ease",
          "& .MuiToolbar-root": {
            minHeight: { xs: 64, sm: 70 },
          },
        }}
      >
        <Toolbar sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
          {/* Logo Section */}
          <Box
            component={Link}
            to="/"
            sx={{
              display: "flex",
              alignItems: "center",
              textDecoration: "none",
              color: "inherit",
              flexGrow: { xs: 1, sm: 0 },
              mr: { sm: 3 },
              "&:hover": {
                opacity: 0.8,
              },
              transition: "opacity 0.2s ease",
            }}
          >
            <Avatar
              src={isDarkMode ? "/logo/logo-dark.png" : "/logo/logo-light.png"}
              alt="Bensco Logo"
              sx={{
                height: { xs: 40, sm: 48 },
                width: { xs: 40, sm: 48 },
                mr: 1.5,
                bgcolor: "transparent",
                p: 0.5,
                transition: "transform 0.3s ease",
                "&:hover": {
                  transform: "scale(1.05) rotate(-5deg)",
                },
              }}
            />
            <Box>
              <Typography
                variant="h6"
                component="div"
                sx={{
                  fontWeight: 800,
                  letterSpacing: 0.5,
                  fontSize: { xs: "1rem", sm: "1.25rem" },
                  background: isDarkMode
                    ? "linear-gradient(135deg, #66bb6a 0%, #4caf50 50%, #2e7d32 100%)"
                    : "linear-gradient(135deg, #2e7d32 0%, #4caf50 50%, #66bb6a 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                Bensco
              </Typography>
              {!isMobile && (
                <Typography
                  variant="caption"
                  sx={{
                    color: "text.secondary",
                    display: "block",
                    lineHeight: 1,
                    fontSize: "0.65rem",
                    letterSpacing: 0.5,
                    textTransform: "uppercase",
                  }}
                >
                  Meeting Management
                </Typography>
              )}
            </Box>
          </Box>

          {/* Navigation Links - Desktop */}
          <Box
            sx={{
              display: { xs: "none", md: "flex" },
              flexGrow: 1,
              ml: 4,
              gap: 0.5,
            }}
          >
            {navItems.map((item) => (
              <Button
                key={item.path}
                component={Link}
                to={item.path}
                color="inherit"
                startIcon={item.icon}
                sx={{
                  fontWeight: isActiveRoute(item.path) ? 700 : 500,
                  px: 2,
                  py: 1,
                  borderRadius: 2,
                  position: "relative",
                  color: isActiveRoute(item.path)
                    ? "primary.main"
                    : "text.primary",
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    bottom: 4,
                    left: "50%",
                    transform: isActiveRoute(item.path)
                      ? "translateX(-50%) scaleX(1)"
                      : "translateX(-50%) scaleX(0)",
                    width: "60%",
                    height: 3,
                    bgcolor: "primary.main",
                    borderRadius: 2,
                    transition: "transform 0.3s ease",
                  },
                  "&:hover": {
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                    "&::after": {
                      transform: "translateX(-50%) scaleX(1)",
                    },
                  },
                }}
              >
                {item.label}
              </Button>
            ))}
          </Box>

          {/* Right Side Actions */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: { xs: 0.5, sm: 1 },
            }}
          >
            {/* Theme Toggle with Tooltip */}
            <Tooltip title={`Switch to ${isDarkMode ? "Light" : "Dark"} Mode`}>
              <IconButton
                onClick={toggleTheme}
                color="inherit"
                aria-label="toggle theme"
                sx={{
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "rotate(30deg) scale(1.1)",
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                  },
                }}
              >
                {isDarkMode ? <Brightness7 /> : <Brightness4 />}
              </IconButton>
            </Tooltip>

            {/* Notifications */}
            <Tooltip title="Notifikasi">
              <IconButton
                onClick={handleNotificationMenu}
                color="inherit"
                aria-label="notifications"
                sx={{
                  transition: "all 0.2s ease",
                  "&:hover": {
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                  },
                }}
              >
                <Badge
                  badgeContent={unreadCount}
                  color="error"
                  sx={{
                    "& .MuiBadge-badge": {
                      animation: unreadCount > 0 ? "pulse 2s infinite" : "none",
                    },
                  }}
                >
                  <Notifications />
                </Badge>
              </IconButton>
            </Tooltip>

            {/* Notification Menu */}
            <Menu
              anchorEl={notificationAnchor}
              open={notificationOpen}
              onClose={handleNotificationClose}
              PaperProps={{
                elevation: 4,
                sx: {
                  width: 360,
                  maxHeight: 420,
                  mt: 1.5,
                  borderRadius: 3,
                  overflow: "hidden",
                  "& .MuiList-root": {
                    p: 0,
                  },
                },
              }}
              transformOrigin={{ horizontal: "right", vertical: "top" }}
              anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
            >
              <Box
                sx={{
                  p: 2,
                  bgcolor: "primary.main",
                  color: "primary.contrastText",
                }}
              >
                <Typography variant="subtitle1" fontWeight="bold">
                  Notifikasi
                </Typography>
                <Typography variant="caption">
                  {unreadCount} notifikasi belum dibaca
                </Typography>
              </Box>
              <Divider />

              {notifications.length === 0 ? (
                <Box sx={{ p: 3, textAlign: "center" }}>
                  <Typography color="text.secondary">
                    Tidak ada notifikasi
                  </Typography>
                </Box>
              ) : (
                notifications.map((notif) => (
                  <MenuItem
                    key={notif.id}
                    onClick={handleNotificationClose}
                    sx={{
                      py: 1.5,
                      px: 2,
                      borderLeft: notif.read
                        ? "none"
                        : `3px solid ${theme.palette.primary.main}`,
                      bgcolor: notif.read
                        ? "transparent"
                        : alpha(theme.palette.primary.main, 0.05),
                      "&:hover": {
                        bgcolor: "action.hover",
                      },
                    }}
                  >
                    <Box>
                      <Typography
                        variant="body2"
                        fontWeight={notif.read ? 400 : 600}
                      >
                        {notif.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {notif.time}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))
              )}

              <Divider />
              <MenuItem
                onClick={handleNotificationClose}
                sx={{ justifyContent: "center" }}
              >
                <Typography variant="caption" color="primary">
                  Lihat semua notifikasi
                </Typography>
              </MenuItem>
            </Menu>

            {/* Desktop User Menu */}
            {!isMobile && (
              <Box sx={{ display: "flex", alignItems: "center", ml: 1 }}>
                <Tooltip title="Akun">
                  <IconButton
                    onClick={handleProfileMenu}
                    size="small"
                    aria-controls={profileOpen ? "profile-menu" : undefined}
                    aria-haspopup="true"
                    aria-expanded={profileOpen ? "true" : undefined}
                    sx={{
                      border: `2px solid ${theme.palette.primary.main}`,
                      transition: "all 0.3s ease",
                      "&:hover": {
                        transform: "scale(1.05)",
                        borderColor: theme.palette.primary.light,
                      },
                    }}
                  >
                    <Avatar
                      alt="User Avatar"
                      src="/avatar/default.jpg"
                      sx={{
                        width: 32,
                        height: 32,
                        bgcolor: "primary.main",
                      }}
                    >
                      <Person />
                    </Avatar>
                  </IconButton>
                </Tooltip>
                <IconButton
                  onClick={handleProfileMenu}
                  size="small"
                  sx={{ ml: 0.5 }}
                >
                  <KeyboardArrowDown />
                </IconButton>

                {/* Profile Menu */}
                <Menu
                  anchorEl={profileAnchor}
                  open={profileOpen}
                  onClose={handleProfileClose}
                  PaperProps={{
                    elevation: 4,
                    sx: {
                      width: 260,
                      mt: 1.5,
                      borderRadius: 3,
                      overflow: "visible",
                      filter: "drop-shadow(0px 4px 20px rgba(0,0,0,0.15))",
                      "&:before": {
                        content: '""',
                        display: "block",
                        position: "absolute",
                        top: 0,
                        right: 20,
                        width: 12,
                        height: 12,
                        bgcolor: "background.paper",
                        transform: "translateY(-50%) rotate(45deg)",
                        zIndex: 0,
                      },
                    },
                  }}
                  transformOrigin={{ horizontal: "right", vertical: "top" }}
                  anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
                >
                  <Box
                    sx={{
                      p: 2,
                      bgcolor: "background.default",
                      borderRadius: "12px 12px 0 0",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Badge
                        overlap="circular"
                        anchorOrigin={{
                          vertical: "bottom",
                          horizontal: "right",
                        }}
                        badgeContent={
                          <Box
                            sx={{
                              width: 14,
                              height: 14,
                              bgcolor: "#4caf50",
                              borderRadius: "50%",
                              border: "2px solid white",
                            }}
                          />
                        }
                      >
                        <Avatar
                          alt="User Avatar"
                          src="/avatar/default.jpg"
                          sx={{ width: 48, height: 48, bgcolor: "#2e7d32" }}
                        >
                          <Person sx={{ fontSize: 28 }} />
                        </Avatar>
                      </Badge>

                      <Box>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography
                            variant="subtitle1"
                            fontWeight="bold"
                            color="text.primary"
                          >
                            Admin
                          </Typography>
                          <Chip
                            label="Admin"
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: "0.6rem",
                              bgcolor: alpha(theme.palette.primary.main, 0.12),
                              color: theme.palette.primary.main,
                              fontWeight: 600,
                              "& .MuiChip-label": { px: 1 },
                            }}
                          />
                        </Stack>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: "block", mt: 0.25 }}
                        >
                          Herwin
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                  <Divider />

                  {/* Profile Menu Items with Dialog */}
                  <MenuItem
                    onClick={() =>
                      handleDialogOpen(
                        dialogData.profile.title,
                        dialogData.profile.content,
                        dialogData.profile.icon,
                      )
                    }
                  >
                    <ListItemIcon>
                      <AccountCircle fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Profil</ListItemText>
                  </MenuItem>

                  <MenuItem
                    onClick={() =>
                      handleDialogOpen(
                        dialogData.settings.title,
                        dialogData.settings.content,
                        dialogData.settings.icon,
                      )
                    }
                  >
                    <ListItemIcon>
                      <Settings fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Pengaturan</ListItemText>
                  </MenuItem>

                  <MenuItem
                    onClick={() =>
                      handleDialogOpen(
                        dialogData.help.title,
                        dialogData.help.content,
                        dialogData.help.icon,
                      )
                    }
                  >
                    <ListItemIcon>
                      <Help fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Bantuan</ListItemText>
                  </MenuItem>

                  <MenuItem
                    onClick={() =>
                      handleDialogOpen(
                        dialogData.feedback.title,
                        dialogData.feedback.content,
                        dialogData.feedback.icon,
                      )
                    }
                  >
                    <ListItemIcon>
                      <Feedback fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Kritik & Saran</ListItemText>
                  </MenuItem>

                  <Divider />

                  <MenuItem
                    onClick={handleLogout}
                    sx={{
                      color: "error.main",
                      "&:hover": {
                        bgcolor: alpha(theme.palette.error.main, 0.08),
                      },
                    }}
                  >
                    <ListItemIcon sx={{ color: "error.main" }}>
                      <ExitToApp fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Keluar</ListItemText>
                  </MenuItem>
                </Menu>
              </Box>
            )}

            {/* Mobile Menu Button */}
            {isMobile && (
              <IconButton
                edge="end"
                color="inherit"
                aria-label="menu"
                onClick={handleMenu}
                sx={{
                  transition: "all 0.3s ease",
                  "&:hover": {
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                  },
                }}
              >
                <MenuIcon />
              </IconButton>
            )}
          </Box>

          {/* Mobile Menu */}
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            onClick={handleClose}
            PaperProps={{
              elevation: 4,
              sx: {
                overflow: "visible",
                filter: "drop-shadow(0px 4px 20px rgba(0,0,0,0.15))",
                mt: 1.5,
                minWidth: 280,
                borderRadius: 3,
                "& .MuiAvatar-root": {
                  width: 32,
                  height: 32,
                  ml: -0.5,
                  mr: 1,
                },
                "&:before": {
                  content: '""',
                  display: "block",
                  position: "absolute",
                  top: 0,
                  right: 14,
                  width: 10,
                  height: 10,
                  bgcolor: "background.paper",
                  transform: "translateY(-50%) rotate(45deg)",
                  zIndex: 0,
                },
              },
            }}
            transformOrigin={{ horizontal: "right", vertical: "top" }}
            anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
          >
            {/* User Info for Mobile */}
            {isMobile && (
              <>
                <Box sx={{ px: 2, py: 1.5 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Avatar>
                      <Person />
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold">
                        John Doe
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        john.doe@email.com
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                <Divider />
              </>
            )}

            {/* Mobile Navigation */}
            {isMobile &&
              navItems.map((item) => (
                <MenuItem
                  key={item.path}
                  component={Link}
                  to={item.path}
                  sx={{
                    bgcolor: isActiveRoute(item.path)
                      ? alpha(theme.palette.primary.main, 0.08)
                      : "transparent",
                    borderLeft: isActiveRoute(item.path)
                      ? `3px solid ${theme.palette.primary.main}`
                      : "none",
                  }}
                >
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText>{item.label}</ListItemText>
                </MenuItem>
              ))}

            {isMobile && <Divider />}

            {/* Mobile Menu Items with Dialog */}
            <MenuItem
              onClick={() => {
                handleDialogOpen(
                  dialogData.profile.title,
                  dialogData.profile.content,
                  dialogData.profile.icon,
                );
                handleClose();
              }}
            >
              <AccountCircle sx={{ mr: 1 }} /> Profil
            </MenuItem>
            <MenuItem
              onClick={() => {
                handleDialogOpen(
                  dialogData.settings.title,
                  dialogData.settings.content,
                  dialogData.settings.icon,
                );
                handleClose();
              }}
            >
              <Settings sx={{ mr: 1 }} /> Pengaturan
            </MenuItem>
            <MenuItem
              onClick={() => {
                handleDialogOpen(
                  dialogData.help.title,
                  dialogData.help.content,
                  dialogData.help.icon,
                );
                handleClose();
              }}
            >
              <Help sx={{ mr: 1 }} /> Bantuan
            </MenuItem>
            <MenuItem
              onClick={() => {
                handleDialogOpen(
                  dialogData.feedback.title,
                  dialogData.feedback.content,
                  dialogData.feedback.icon,
                );
                handleClose();
              }}
            >
              <Feedback sx={{ mr: 1 }} /> Kritik & Saran
            </MenuItem>

            <Divider />

            <MenuItem
              onClick={handleLogout}
              sx={{
                color: "error.main",
                "&:hover": {
                  bgcolor: alpha(theme.palette.error.main, 0.08),
                },
              }}
            >
              <ExitToApp sx={{ mr: 1 }} /> Keluar
            </MenuItem>
          </Menu>
        </Toolbar>

        {/* Add custom animation keyframes */}
        <style>
          {`
            @keyframes pulse {
              0% { transform: scale(1); }
              50% { transform: scale(1.1); }
              100% { transform: scale(1); }
            }
          `}
        </style>
      </MuiAppBar>

      {/* Dialog Component */}
      <Dialog
        open={dialogOpen}
        onClose={handleDialogClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            overflow: "hidden",
            boxShadow: `0 8px 40px ${alpha(theme.palette.common.black, 0.15)}`,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            bgcolor: alpha(theme.palette.primary.main, 0.04),
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
            pb: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 48,
                height: 48,
                borderRadius: "50%",
                bgcolor: alpha(theme.palette.primary.main, 0.1),
              }}
            >
              {dialogIcon}
            </Box>
            <Typography variant="h6" fontWeight="bold">
              {dialogTitle}
            </Typography>
          </Box>
          <IconButton
            onClick={handleDialogClose}
            sx={{
              color: "text.secondary",
              "&:hover": {
                bgcolor: alpha(theme.palette.error.main, 0.08),
                color: "error.main",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ py: 4, px: 3 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 3,
            }}
          >
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: "50%",
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "primary.main",
              }}
            >
              {dialogIcon}
            </Box>

            <DialogContentText
              sx={{
                textAlign: "center",
                color: "text.secondary",
                fontSize: "1rem",
                lineHeight: 1.8,
              }}
            >
              {dialogContent}
            </DialogContentText>

            <Paper
              elevation={0}
              sx={{
                p: 2,
                bgcolor: alpha(theme.palette.info.main, 0.04),
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.info.main, 0.15)}`,
                width: "100%",
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                align="center"
                display="block"
              >
                💡 Fitur ini sedang dalam pengembangan
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                align="center"
                display="block"
                sx={{ mt: 0.5 }}
              >
                Segera hadir di update berikutnya
              </Typography>
            </Paper>
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            py: 2,
            borderTop: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
            bgcolor: alpha(theme.palette.background.default, 0.5),
          }}
        >
          <Button
            onClick={handleDialogClose}
            variant="contained"
            sx={{
              borderRadius: 3,
              px: 4,
              py: 1,
              bgcolor: "#2e7d32",
              "&:hover": {
                bgcolor: "#1b5e20",
              },
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            Tutup
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
